<template>
  <div class="input-matrix-radio" :id="id">
    <div>
      <div/>
      <ul>
        <li v-for="(choice, idx) in choices" :key="`choice-${idx}`">
          <span>{{ choice.label }}</span>
        </li>
      </ul>
    </div>
    <div>
      <div
        v-for="(item, itemIndex) in ivalue"
        :key="`item-${itemIndex}`"
        :class="{ dynamic: item.dynamic }">
          <template v-if="!item.dynamic">
            <div class="label">
              <a
                v-if="item.code && item.lotse"
                :href="`https://www.rehadat-icf.de/de/klassifikation${item.lotse}`"
                target="_blank"
                class="code">
                {{ item.code }}
              </a>
              <span v-else-if="item.code" class="code">{{ item.code }}</span>
              {{ item.label }}
              <Tooltip
                v-if="item.info"
                icon="info"
                :content="item.info"
                :for-state="forState"
                :for-report="forReport"
              />
            </div>
          </template>
          <template v-else>
            <div>
              <TextInput
                :value.sync="item.label"
                fill
                :autofocus="!reviewMode"
              />
              <button class="action" @click="removeItem(itemIndex)">{{ $t('labels.delete') }}</button>
            </div>
          </template>
          <div class="choices">
            <Radio
              v-for="(choice, choiceIndex) in choices"
              :key="`item-${itemIndex}-choice-${choiceIndex}`"
              :value="choice.value"
              :checked.sync="ivalue[itemIndex].value"
              :class="{ changed: ichanges[itemIndex] === choice.value }"
            />
          </div>
      </div>
      <div v-if="dynamic">
        <button class="action" @click="addItem">{{ dynamicLabel }}</button>
      </div>
    </div>
    <template v-if="reviewMode">
      <button class="adopt" @click.stop.prevent="handleAdopt(true)">{{ $t('review.diff.adopt') }}</button>
      <button class="adopt" @click.stop.prevent="handleAdopt(false)">{{ $t('review.diff.ignore') }}</button>
    </template>
  </div>
</template>

<script>
  import Radio from '@/components/ui/inputs/Radio';
  import Tooltip from '@/components/ui/Tooltip';
  import TextInput from '@/components/ui/inputs/TextInput';

  let uid = 0;

  export default {
    name: 'RadioMatrix',
    components: {
      TextInput,
      Tooltip,
      Radio,
    },
    //---------------------------------------------------
    //
    //  Properties
    //
    //---------------------------------------------------
    props: {
      value: {
        type: Array,
        default: () => [],
      },
      choices: {
        type: Array,
        default: () => [],
      },
      dynamic: {
        type: Boolean,
        default: false,
      },
      dynamicLabel: {
        type: String,
        default: null,
      },
      forState: {
        type: Boolean,
        default: false,
      },
      forReport: {
        type: Boolean,
        default: false,
      },
      required: {
        type: Boolean,
        default: false,
      },
      reviewMode: {
        type: Boolean,
        default: false,
      },
      reviewId: {
        type: String,
        default: null,
      },
      reviewData: {
        type: Object,
        default: null,
      },
    },
    //---------------------------------------------------
    //
    //  Data model
    //
    //---------------------------------------------------
    data() {
      uid += 1;
      return {
        id: `radio-matrix-${uid}`,
        ivalue: [],
        oldvalues: [],
        ichanges: [],
        preventValueChanges: false,
      };
    },
    //---------------------------------------------------
    //
    //  Computed Properties
    //
    //---------------------------------------------------
    computed: {},
    //---------------------------------------------------
    //
    //  Watch Properties
    //
    //---------------------------------------------------
    watch: {
      value(val) {
        this.preventValueChanges = true;
        this.ivalue = val;
        if (this.reviewMode) {
          this.parseChanges();
        } else {
          this.$nextTick(() => {
            this.preventValueChanges = false;
          });
        }
      },
      ivalue: {
        handler(val) {
          if (!this.reviewMode && !this.preventValueChanges) {
            this.$emit('update:value', val);
            this.$emit('input', val);
          }
        },
        deep: true,
      },
      reviewMode: {
        handler(bool) {
          if (bool === false) {
            this.ichanges = [];
          }
        },
        immediate: true,
      },
    },
    //---------------------------------------------------
    //
    //  Filter Properties
    //
    //---------------------------------------------------
    // filters: {},
    //---------------------------------------------------
    //
    //  Validation Properties
    //
    //---------------------------------------------------
    // validations: {},
    //---------------------------------------------------
    //
    //  Vue Lifecycle
    //
    //---------------------------------------------------
    // beforeCreate() {},
    created() {
      this.preventValueChanges = true;
      this.ivalue = JSON.parse(JSON.stringify(this.value || []));
      if (this.reviewMode) {
        this.parseChanges();
      } else {
        this.$nextTick(() => {
          this.preventValueChanges = false;
        });
      }
    },
    // beforeMount() {},
    // render(h) { return h(); },
    // mounted() {},
    // beforeUpdate() {},
    // updated() {},
    // beforeDestroy() {},
    // destroyed() {},
    //---------------------------------------------------
    //
    //  Methods
    //
    //---------------------------------------------------
    methods: {
      addItem() {
        this.ivalue.push({ label: null, dynamic: true });
      },
      removeItem(idx) {
        const ivalue = this.ivalue.slice();
        ivalue.splice(idx, 1);
        this.ivalue = ivalue;
      },
      //----------------------------------
      // Review Methods
      //----------------------------------
      parseChanges() {
        const reviewId = this.reviewId;
        const reviewData = JSON.parse(JSON.stringify(this.reviewData));
        if (reviewData && reviewId) {
          const oldvalues = JSON.parse(JSON.stringify(this.ivalue));
          const newvalues = reviewData[reviewId];

          const ichanges = [];
          newvalues.forEach((entry, idx) => {
            const newvalue = entry?.value;
            const oldvalue = oldvalues[idx]?.value;
            ichanges[idx] = null;
            if (newvalue !== oldvalue) {
              ichanges[idx] = oldvalue;
            }
          });

          this.ivalue = newvalues;
          this.oldvalues = oldvalues;

          if (ichanges.length > 0 && ichanges.find(o => o !== null)) {
            this.ichanges = ichanges;
          }
        }
        this.preventValueChanges = false;
      },
      //----------------------------------
      // Event Handlers
      //----------------------------------
      handleAdopt(accept) {
        const newvalues = JSON.parse(JSON.stringify((this.ivalue)));
        const oldvalues = JSON.parse(JSON.stringify((this.oldvalues)));
        this.$emit('adopt', { accept, value: accept ? newvalues : oldvalues });
      },
    },
  };
</script>

<style lang="scss">
@import '~scss/mixins';
@import '~scss/variables';

.input-matrix-radio {
  & > div {
    &:first-of-type {
      display: flex;
      justify-content: space-between;
      width: 55vw;
      max-width: 808px;
      padding: 0 16px;

      ul {
        display: flex;
        justify-content: flex-end;
        list-style-type: none;
        min-width: 288px;
        margin: 0;
        padding: 0;

        li {
          position: relative;
          padding: 0;
          margin: 0;
          width: 16px;
          height: 22px;
          margin-right: 64px;
          &:last-of-type {
            margin-right: 0;
          }

          & > span {
            top: 0;
            left: 0;
            display: block;
            white-space: nowrap;
            position: absolute;
            transform: rotate(-30deg);
            transform-origin: 0 0;
            @include font-body-small;
            user-select: none;
            pointer-events: none;
            color: $color-black-alpha-80;
          }
        }
      }
    }

    &:last-of-type {
      border: 1px solid $color-black-solid-05;
      width: 55vw;
      max-width: 808px;

      & > div {
        display: flex;
        justify-content: space-between;
        align-items: center;
        background-color: transparent;
        will-change: background-color;
        transition: background-color $default-transition;
        padding: 12px 16px;

        &:hover {
          background-color: $color-blue-alpha-10;
          &.action { background-color: transparent; }
        }

        & > .label {
          @include font-body-medium;
          color: $color-black-solid-100;

          & > span.code, & > a.code {
            display: inline-block;
            font-weight: bold;
            font-size: 1.1rem;
            position: relative;
            margin-right: 2px;
            color: $color-blue-solid-100;
            border: 1px solid $color-blue-solid-100;
            background-color: $color-white-solid-10;
            padding: 0 4px;
            border-radius: $default-border-radius;
            transform: scale(0.9);
            text-decoration: none;
            user-select: none;
          }

          & > a.code:hover {
            cursor: help;
            background-color: $color-blue-alpha-20;
          }

          & > .comp-tooltip {
            margin-left: 8px;
          }
        }

        & > .choices {
          min-width: 288px;
          display: inline-flex;
          justify-content: flex-end;

          & > .input-radio {
            margin-right: 64px;
            &:last-of-type {
              margin-right: 0;
            }
          }
        }
      }

      & > div.dynamic {
        padding: 6px 16px;
        & > div:first-of-type {
          width: 100%;
          .input-text { width: 65%; height: 40px; }
          button.action { margin-left: 8px; }
        }
      }
    }
  }

  button.action {
    background: transparent;
    outline: 0;
    border: 0;
    padding: 0;
    margin: 0;
    @include font-body-small;
    color: $color-black-alpha-60;
    text-decoration: underline;
    cursor: pointer;
  }

  & > button.adopt {
    display: inline-block;
    outline: 0;
    border: 0;
    cursor: pointer;
    background: transparent;
    text-decoration: underline;
    margin: 16px 0 0;
    @include font-body-small;
    color: $color-black-alpha-60;
  }
}
</style>
